import AudienceGroupPicker from '@/components/audience/AudienceGroupPicker';
import { useAudienceCatalog } from '@/hooks/useAudienceCatalog';
import { useBranding } from '@/context/BrandingContext';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import LocationTargetSelector from '@/components/ads/LocationTargetSelector';
import { userSelectionColumns } from '@/components/users/userSelectionColumns';
import { useTranslation } from '@/hooks/useTranslation';
import Select from '@/components/ui/Select';
import { useAdCampaign, useDisplayAdCampaignNow } from '@/hooks/useAdCampaigns';
import { useAppUsers } from '@/hooks/useUsers';
import { AdCampaignDisplayNowMode, AdCampaignLocationTarget } from '@/types/ads';

type DisplayNowModalProps = {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string;
  campaignName: string;
};

const DisplayNowModal = ({ isOpen, onClose, campaignId, campaignName }: DisplayNowModalProps) => {
  const { t } = useTranslation();
  const campaign = useAdCampaign(isOpen ? campaignId : '');
  const [creativeId, setCreativeId] = useState('');
  const [contentType, setContentType] = useState<'LIVE' | 'VOD' | 'RADIO'>('LIVE');
  const available = campaign.data?.creatives?.filter(c => c.enabled && (c.content.showOnLiveTv || c.content.showOnVod || c.content.showOnRadio)) ?? [];
  const selected = available.find(c => c.id === creativeId) ?? available[0];
  const destinations = selected ? [selected.content.showOnLiveTv && 'LIVE', selected.content.showOnVod && 'VOD', selected.content.showOnRadio && 'RADIO'].filter(Boolean) as Array<'LIVE' | 'VOD' | 'RADIO'> : [];
  const destination = destinations.includes(contentType) ? contentType : destinations[0];
  const selection = selected ? { creativeId: selected.id, contentType: destination } : {};
  const { branding } = useBranding();
  const [displayNowMode, setDisplayNowMode] = useState<AdCampaignDisplayNowMode>('ALL');
  const [displayNowUsers, setDisplayNowUsers] = useState<string[]>([]);
  const [displayNowLocations, setDisplayNowLocations] = useState<AdCampaignLocationTarget[]>([]);
  const [displayNowGroups, setDisplayNowGroups] = useState<string[]>([]);
  const catalog = useAudienceCatalog(isOpen && Boolean(branding.modules?.audienceManagementEnabled));
  const displayNowMutation = useDisplayAdCampaignNow();

  useEffect(() => {
    if (!isOpen) return;
    setDisplayNowMode('ALL');
    setCreativeId('');
    setDisplayNowUsers([]);
    setDisplayNowLocations([]);
    setDisplayNowGroups([]);
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleDisplayNow = async () => {
    if (!campaignId || campaign.isLoading || campaign.isError || (campaign.data?.creatives?.length && !selected)) return;
    if (displayNowMode === 'USERS') {
      const selectedIds = displayNowUsers
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      if (selectedIds.length === 0) {
        toast.error('Selecciona al menos un usuario');
        return;
      }
      try {
        await displayNowMutation.mutateAsync({
          id: campaignId,
          data: { ...selection, mode: 'USERS', userIds: selectedIds },
        });
        toast.success(`Solicitud enviada`);
        handleClose();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'No se pudo enviar la solicitud');
      }
      return;
    }

    if (displayNowMode === 'LOCATION') {
      if (displayNowLocations.length === 0 && displayNowGroups.length === 0) {
        toast.error(t.audience.selectDisplayTarget);
        return;
      }
      try {
        await displayNowMutation.mutateAsync({
          id: campaignId,
          data: { ...selection, mode: 'LOCATION', locationTargets: displayNowLocations, nodeIds: displayNowGroups },
        });
        toast.success(`Solicitud enviada`);
        handleClose();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'No se pudo enviar la solicitud');
      }
      return;
    }

    try {
      await displayNowMutation.mutateAsync({
        id: campaignId,
        data: { ...selection, mode: 'ALL' },
      });
      toast.success(`Solicitud enviada`);
      handleClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo enviar la solicitud');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Mostrar campaña ahora" size="xl">
      <div className="space-y-4">
        <div className="rounded-md border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <span className="font-semibold">Campaña:</span> <span>{campaignName}</span>
        </div>
        {!!available.length && <div className="grid gap-3 sm:grid-cols-2">
          <Select label={t.adCreatives.creative} value={selected?.id ?? ''} onChange={event => setCreativeId(event.target.value)}
            options={available.map(c => ({ value: c.id ?? '', label: c.name }))} />
          <Select label={t.adCreatives.destinations} value={destination ?? ''} onChange={event => setContentType(event.target.value as 'LIVE' | 'VOD' | 'RADIO')}
            options={destinations.map(d => ({ value: d, label: d === 'LIVE' ? t.adCreatives.live : d === 'VOD' ? t.adCreatives.vod : t.adCreatives.radio }))} />
        </div>}
        <div className="space-y-3">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Alcance</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { value: 'ALL', label: 'Todos' },
                { value: 'USERS', label: 'Usuarios específicos' },
                { value: 'LOCATION', label: 'Ubicación o Grupo' },
              ].filter(option => option.value !== 'LOCATION' || branding.modules?.audienceManagementEnabled).map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                    displayNowMode === option.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="displayNowMode"
                    value={option.value}
                    checked={displayNowMode === option.value}
                    onChange={() => {
                      setDisplayNowMode(option.value as AdCampaignDisplayNowMode);
                      setDisplayNowUsers([]);
                      setDisplayNowLocations([]);
    setDisplayNowGroups([]);
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {displayNowMode === 'ALL' && (
            <p className="text-sm text-gray-500">
              Se enviará la campaña a todos los usuarios activos con dispositivo vinculado.
            </p>
          )}

          {displayNowMode === 'USERS' && (
            <div className="space-y-2">
              <div className="text-sm text-gray-500">
                Seleccionados: <span className="font-semibold text-gray-900">{displayNowUsers.length}</span>
              </div>
              <DataTableWithQuery
                columns={userSelectionColumns}
                actions={[]}
                useQueryHook={useAppUsers}
                itemsPerPage={5}
                title=""
                searchPlaceholder="Buscar por nombre o usuario..."
                tableStyle="compact"
                enableRowSelection
                selectedRowIds={displayNowUsers}
                onSelectionChange={setDisplayNowUsers}
                getRowId={(user) => String(user.id)}
                className="shadow-none border border-gray-200"
              />
            </div>
          )}

          {displayNowMode === 'LOCATION' && (
            <div className="space-y-4">
              <LocationTargetSelector value={displayNowLocations} onChange={setDisplayNowLocations} />
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">{t.audience.serviceGroup}</h3>
                {catalog.isPending ? <p>{t.audience.loading}</p> : catalog.isError ? <p role="alert">{t.audience.loadError}</p> :
                  <AudienceGroupPicker nodes={catalog.data?.nodes ?? []} label={t.audience.serviceGroup} hideLabel pillTrigger
                    selected={displayNowGroups} onChange={setDisplayNowGroups} />}
              </div>
              <p className="text-xs text-gray-500">{t.audience.displayTargetHelp}</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleDisplayNow}
            disabled={
              displayNowMutation.isPending || campaign.isLoading || campaign.isError || (!!campaign.data?.creatives?.length && !selected)
              || (displayNowMode === 'USERS' && displayNowUsers.length === 0)
              || (displayNowMode === 'LOCATION' && displayNowLocations.length === 0 && displayNowGroups.length === 0)
            }
          >
            Mostrar ahora
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DisplayNowModal;
